
import React, { useEffect, useCallback, useRef } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import type { GeoJSON as LeafletGeoJSON } from 'leaflet';
import axios from 'axios';

type Props = {
  url: string;
  color: string;
  minZoom?: number;
};

export const GeoLayer = ({
                           url,
                           color,
                           minZoom = 10,
                         }: Props) => {
  const map = useMap();

  const layerRef = useRef<LeafletGeoJSON>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const layer = layerRef.current;

    // Request only if this layer is selected
    if (!layer || !map.hasLayer(layer)) {
      return;
    }

    const currentRequest = ++requestId.current;
    const zoom = map.getZoom();

    // Clear polygons below minimum zoom
    if (zoom < minZoom) {
      layer.clearLayers();
      return;
    }

    const bounds = map.getBounds();

    try {
      const res = await axios.get(url, {
        params: {
          zoom,
          fromLatitude: bounds.getSouth(),
          fromLongitude: bounds.getWest(),
          toLatitude: bounds.getNorth(),
          toLongitude: bounds.getEast(),
        },
      });

      // Ignore outdated requests or unchecked layers
      if (
        currentRequest !== requestId.current ||
        !map.hasLayer(layer)
      ) {
        return;
      }

      const geojson =
        res.data?.body?.parcels ??
        res.data?.body?.blocks ??
        res.data?.body;

      let features: any[] = [];

      // Support FeatureCollection
      if (geojson?.type === 'FeatureCollection') {
        features = geojson.features || [];
      }
      // Support single Feature
      else if (geojson?.type === 'Feature') {
        features = [geojson];
      }
      // Support array of Features
      else if (Array.isArray(geojson)) {
        features = geojson;
      }

      // Update existing Leaflet layer
      layer.clearLayers();

      layer.addData({
        type: 'FeatureCollection',
        // @ts-ignore
        features,
      });

    } catch (error) {
      if (currentRequest !== requestId.current) {
        return;
      }

      console.error('Failed loading geo layer:', error);
    }
  }, [map, url, minZoom]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const isSelected = () => {
      return !!layerRef.current &&
        map.hasLayer(layerRef.current);
    };

    // This overlay was selected
    const handleOverlayAdd = (event: any) => {
      if (event.layer === layerRef.current) {
        load();
      }
    };

    // This overlay was unchecked
    const handleOverlayRemove = (event: any) => {
      if (event.layer === layerRef.current) {
        requestId.current++;
        clearTimeout(timeout);
      }
    };

    // Reload only selected layers
    const handleMoveEnd = () => {
      if (!isSelected()) {
        return;
      }

      clearTimeout(timeout);
      timeout = setTimeout(load, 200);
    };

    map.on('overlayadd', handleOverlayAdd);
    map.on('overlayremove', handleOverlayRemove);
    map.on('moveend', handleMoveEnd);

    // Handle initially selected layers
    if (isSelected()) {
      load();
    }

    return () => {
      requestId.current++;
      clearTimeout(timeout);

      map.off('overlayadd', handleOverlayAdd);
      map.off('overlayremove', handleOverlayRemove);
      map.off('moveend', handleMoveEnd);
    };
  }, [map, load]);

  return (
    <GeoJSON
      ref={layerRef}
      data={{
        type: 'FeatureCollection',
        // @ts-ignore
        features: [],
      }}
      style={() => ({
        color,
        weight: 3,
        fillOpacity: 0.1,
      })}
      onEachFeature={(feature: any, layer: any) => {
        const p = feature.properties || {};

        layer.bindPopup(`
          <div style="font-size:24px;">
            <p style="text-align:right;margin:0;">
              <b style="margin-left:1rem">חלקה:</b>
              ${p.PARCEL || '-'}
            </p>
            <p style="text-align:right;margin:0;">
              <b style="margin-left:1rem">גוש:</b>
              ${p.GUSH_NUM || '-'}
            </p>
          </div>
        `);

        layer.on('mouseover', () => {
          layer.setStyle({
            weight: 3,
            color: 'rgb(253, 185, 19)',
            fillOpacity: 0.25,
          });
        });

        layer.on('mouseout', () => {
          layer.setStyle({
            weight: 3,
            color,
            fillOpacity: 0.05,
          });
        });

        layer.on('click', () => {
          layer.setStyle({
            color: 'rgb(253, 185, 19)',
            weight: 3,
            fillOpacity: 0.2,
          });
        });
      }}
    />
  );
};
